import { useState, useEffect, useCallback, useMemo } from 'react';
import { authenticatedFetch } from '@/lib/api';
import { filterValidGroupIds } from '@/lib/utils';
import type { DeploymentPeriod } from '@/types/history';

interface AggregatedDeployment {
  company_id: string;
  company_name: string;
  is_internal: boolean;
  started_at: string;
  ended_at: string | null;
  unit_count: number;
  is_current: boolean;
}

interface GroupDeploymentsResponse {
  deployments: DeploymentPeriod[];
  aggregated: AggregatedDeployment[];
  unitCount: number;
}

interface UseGroupDeploymentsOptions {
  groupIds: string[];
  startDate?: Date | null;
  endDate?: Date | null;
  enabled?: boolean;
}

interface UseGroupDeploymentsReturn {
  deployments: DeploymentPeriod[];
  aggregatedDeployments: AggregatedDeployment[];
  unitCount: number;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook to fetch deployment history for units within selected groups
 * Used for chart annotations showing deployment periods
 *
 * Only returns data for internal users (superadmin/admin)
 * External users receive empty arrays
 */
export function useGroupDeployments({
  groupIds,
  startDate,
  endDate,
  enabled = true,
}: UseGroupDeploymentsOptions): UseGroupDeploymentsReturn {
  const [deployments, setDeployments] = useState<DeploymentPeriod[]>([]);
  const [aggregatedDeployments, setAggregatedDeployments] = useState<AggregatedDeployment[]>([]);
  const [unitCount, setUnitCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Serialize dependencies for stable comparison
  const groupIdsKey = useMemo(() => groupIds.join(','), [groupIds]);
  const startDateKey = useMemo(() => startDate?.toISOString() ?? '', [startDate]);
  const endDateKey = useMemo(() => endDate?.toISOString() ?? '', [endDate]);

  const fetchDeployments = useCallback(async () => {
    // Filter out synthetic __unassigned_* IDs
    const validGroupIds = filterValidGroupIds(groupIds);

    if (!enabled || validGroupIds.length === 0) {
      setDeployments([]);
      setAggregatedDeployments([]);
      setUnitCount(0);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      params.set('groupIds', validGroupIds.join(','));

      if (startDate) {
        params.set('startDate', startDate.toISOString().split('T')[0]);
      }
      if (endDate) {
        params.set('endDate', endDate.toISOString().split('T')[0]);
      }

      const response = await authenticatedFetch(
        `/api/v1/admin/groups/deployments?${params.toString()}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch group deployments');
      }

      const data: GroupDeploymentsResponse = await response.json();

      setDeployments(data.deployments || []);
      setAggregatedDeployments(data.aggregated || []);
      setUnitCount(data.unitCount || 0);
    } catch (err) {
      console.error('[useGroupDeployments] Failed to fetch:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch deployments'));
      setDeployments([]);
      setAggregatedDeployments([]);
      setUnitCount(0);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, groupIdsKey, startDateKey, endDateKey]);

  // Fetch on mount and when dependencies change
  useEffect(() => {
    fetchDeployments();
  }, [fetchDeployments]);

  return {
    deployments,
    aggregatedDeployments,
    unitCount,
    isLoading,
    error,
    refetch: fetchDeployments,
  };
}
